import { useCallback, useEffect } from "react";
import { useFlow } from "@rapid/contexts/FlowContext";
import Button from "@scripts/react/components/Button";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { SidebarActions } from "@rapid/components/layout/SidebarActions";
import { BuildSteps, Flows } from "@rapid/types/flows";
import { AnalyticsFlowEvent } from "@rapid/analytics/types";
import { DEFAULT_DURATION } from "@rapid/constants/motion";
import { TestWithMoreFilesActionCard } from "./TestWithMoreFilesActionCard";
import { useModel } from "@rapid/contexts/ModelContext";
import { useGetPredictions } from "@rapid/hooks/useGetPredictions";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { AdjustPredictionsActionCard } from "./AdjustPredictionsActionCard";
import { AnnotationSummaryActionCard } from "./AnnotationSummaryActionCard";
import { ImproveActionCard } from "./ImproveActionCard";
import { useReviewTab } from "../ReviewContext";
import { ReviewTab } from "../ReviewTabs";
import { PublishActionCard } from "./PublishComponents";
import { useSignInAndCreateWorkflow } from "@rapid/hooks/useSignInAndCreateWorkflow";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { usePageNavigationShortcut } from "@rapid/hooks/usePageNavigationShortcut";
import { useStopGeneratingPredictions } from "@rapid/hooks/useStopGeneratingPredictions";
import { useClearAllPredictions } from "@rapid/store";
import { Divider } from "@scripts/react/components/Divider";

const ButtonComponent = Button as any;
const DividerComponent = Divider as any;

interface ReviewActionButtonsProps {
    forceVideoFlow?: boolean;
}

export const ReviewActionButtons = ({ forceVideoFlow = false }: ReviewActionButtonsProps) => {
    const { activeTab } = useReviewTab();
    const isAnnotationsView = activeTab === ReviewTab.MyAnnotations;

    // Use legacy hook for data access and optimized hooks for other features
    const { annotationsForFileAndFrameId, task } = useFlow();
    const { setStep } = useFlowState();
    const { isDownloading, isTestingNewFiles, setIsTestingNewFiles } = useModel();
    const { loading: signInLoading } = useSignInAndCreateWorkflow();
    const clearAllPredictions = useClearAllPredictions();

    // Reset isTestingNewFiles when component mounts (entering Review step)
    useEffect(() => {
        setIsTestingNewFiles(false);
    }, [setIsTestingNewFiles]);

    const isActionDisabled = isDownloading || isTestingNewFiles || signInLoading;

    // Analytics
    const { track } = useAnalytics();

    const { stopGeneratingPredictions } = useStopGeneratingPredictions();
    const handleConfirmRelabel = useCallback(() => {
        stopGeneratingPredictions();
        setStep({ flow: Flows.BUILD, step: BuildSteps.PROMPT });
        track({
            event: AnalyticsFlowEvent.REPROMPT_CLICKED,
            data: {
                task,
                prompted_files_count: Object.keys(annotationsForFileAndFrameId).length,
                prompts_count: Object.values(annotationsForFileAndFrameId).reduce(
                    (acc: number, annotationsForFile: any) =>
                        acc + Object.values(annotationsForFile).length,
                    0
                )
            }
        });
        // Clear predictions for all upload intents
        clearAllPredictions();
    }, [
        annotationsForFileAndFrameId,
        setStep,
        track,
        task,
        stopGeneratingPredictions,
        clearAllPredictions
    ]);

    // UGLY hack, but if there is an error in model generation, we switch back to the prompt step where the error is displayed
    // TODO(japrescott, 2025-09-10): Better error handling not relying on the inference queue state
    const { isLoadingInferenceQueue, errorInferenceQueue } = useGetPredictions();
    useEffect(() => {
        if (!isLoadingInferenceQueue && errorInferenceQueue) {
            console.log("WOOOPS, got an error on the inference queue, going back to prompt step", {
                errorInferenceQueue
            });
            setStep({ flow: Flows.BUILD, step: BuildSteps.PROMPT });
        }
    }, [errorInferenceQueue, isLoadingInferenceQueue, setStep]);

    // Keyboard shortcut: Command + Left Arrow
    usePageNavigationShortcut("left", handleConfirmRelabel, isDownloading);

    return (
        <>
            <div className="content gap-3">
                <PublishActionCard disabled={isActionDisabled} />
                <ImproveActionCard disabled={isActionDisabled} />
                <DividerComponent text="OR" />
                <TestWithMoreFilesActionCard disabled={isActionDisabled} />
                {isAnnotationsView ? (
                    <AnnotationSummaryActionCard disabled={isActionDisabled} />
                ) : (
                    <AdjustPredictionsActionCard disabled={isActionDisabled} />
                )}
            </div>

            {/* Desktop CTAs */}
            <SidebarActions
                toolbarProps={{
                    initial: { opacity: 0 },
                    animate: { opacity: 1 },
                    transition: { duration: DEFAULT_DURATION.slower, ease: "easeOut" }
                }}
            >
                <ButtonComponent
                    type="secondary"
                    onClick={handleConfirmRelabel}
                    disabled={isDownloading}
                >
                    <i className="far fa-arrow-left"></i>
                    <span>Add New Objects</span>
                    <div
                        className={twClassNames("hotkeysContainer", isDownloading && "opacity-30")}
                    >
                        <span className="hotkey">⌘</span>
                        <span className="hotkey">←</span>
                    </div>
                </ButtonComponent>
            </SidebarActions>
        </>
    );
};
